/*
  Fitties (2026-10-06): the theme's own consent banner, used instead of Shopify's cookie banner.
  Consent is recorded exactly as Shopify's banner records it (shopifycloud/privacy-banner/storefront-banner.js):
  - Accept and Accept all: analytics, marketing and preferences all yes.
  - Decline and Decline all: all three no.
  - Save my choices: each of the three as ticked. Required is always on.
  setTrackingConsent fires Shopify's visitorConsentCollected event, which the theme's Microsoft Clarity
  snippet (snippets/microsoft-clarity.liquid) and Shopify's pixels already listen to.

  When it shows: only while the visitor hasn't chosen, and only where Shopify requires consent
  (Settings > Customer privacy: the EEA, UK and Switzerland). There nothing is allowed until the
  visitor chooses, so userCanBeTracked() is false; shouldShowBanner() is checked too. It shows on
  every storefront page, the cart included, except the password page.

  Reopening: the footer's "Cookie preferences" link and any link to #shopifyReshowConsentBanner
  open the preferences view. The footer link shows only where consent is required, or to a
  visitor who has already chosen.

  Checking the look: ?cookie_preview=1 shows the banner anywhere. Its buttons still record a choice.
*/
class CookieBanner extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      activeClass: 'is-active',
      closingClass: 'is-closing'
    };
    this.banner = this.querySelector('.cookie-banner');
    this.card = this.querySelector('.cookie__card');
    this.views = {
      banner: this.querySelector('[data-cookie-view="banner"]'),
      preferences: this.querySelector('[data-cookie-view="preferences"]')
    };
    this.testMode = this.dataset.testMode === 'true';
    this.preview = new URLSearchParams(window.location.search).get('cookie_preview') === '1';
    this.ready = false;
    this.openedFrom = 'banner';

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('keydown', this.onKeyDown.bind(this));
    document.addEventListener('click', this.onReopenClick.bind(this));

    if (this.testMode) {
      this.init();
      return;
    }

    window.Shopify.loadFeatures([
      {
        name: 'consent-tracking-api',
        version: '0.1',
      }
    ],
    (error) => {
      if (error) {
        throw error;
      }

      this.ready = true;
      this.beforeInit();
    });
  }

  connectedCallback() {
    if (Shopify.designMode) {
      this.onShopifySectionLoad = this.onSectionLoad.bind(this);
      this.onShopifySectionSelect = this.onSectionSelect.bind(this);
      this.onShopifySectionDeselect = this.onSectionDeselect.bind(this);
      document.addEventListener('shopify:section:load', this.onShopifySectionLoad);
      document.addEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.addEventListener('shopify:section:deselect', this.onShopifySectionDeselect);
    }
  }
  disconnectedCallback() {
    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:load', this.onShopifySectionLoad);
      document.removeEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onShopifySectionDeselect);
    }
  }
  onSectionLoad(event) {
    filterShopifyEvent(event, this, () => this.open.bind(this));
  }
  onSectionSelect(event) {
    filterShopifyEvent(event, this, () => this.open('banner'));
  }
  onSectionDeselect(event) {
    filterShopifyEvent(event, this, this.close.bind(this));
  }

  privacy() {
    return window.Shopify && window.Shopify.customerPrivacy;
  }

  beforeInit() {
    const privacy = this.privacy();
    const noChoice = privacy.getTrackingConsent() === 'no_interaction';
    const required = privacy.shouldShowBanner() || !privacy.userCanBeTracked();

    this.toggleReopenLinks(required || !noChoice);

    if (window.location.pathname.match(/\/password$/)) return;
    if (this.preview || (required && noChoice)) {
      this.init();
    }
  }

  init() {
    if (Shopify && Shopify.designMode) {
      return;
    }

    setTimeout(() => this.open('banner'), (parseInt(this.dataset.delay, 10) || 0) * 1000);
  }

  showView(name) {
    Object.keys(this.views).forEach((key) => {
      if (this.views[key]) this.views[key].hidden = key !== name;
    });
    const heading = this.views[name] && this.views[name].querySelector('.cookie__heading');
    if (heading && heading.id) this.card.setAttribute('aria-labelledby', heading.id);
  }

  open(view) {
    const name = view === 'preferences' ? 'preferences' : 'banner';
    this.showView(name);
    if (name === 'preferences') this.syncChoices();
    this.banner.classList.remove(this.classes.closingClass);
    this.banner.classList.add(this.classes.activeClass);
    if (name === 'preferences') {
      const heading = this.views.preferences.querySelector('.cookie__heading');
      if (heading) heading.focus({ preventScroll: true });
    }
  }

  close() {
    this.banner.classList.add(this.classes.closingClass);

    setTimeout(() => {
      this.banner.classList.remove(this.classes.activeClass);
      this.banner.classList.remove(this.classes.closingClass);
    }, 500);
  }

  // Tick the boxes to match the visitor's current choices, as Shopify's preferences window does.
  syncChoices() {
    const privacy = this.privacy();
    const current = this.ready && privacy && privacy.currentVisitorConsent ? privacy.currentVisitorConsent() : {};
    this.querySelectorAll('[data-cookie-purpose]').forEach((input) => {
      input.checked = current[input.dataset.cookiePurpose] === 'yes';
    });
  }

  record(consent) {
    this.close();
    if (this.testMode || !this.ready) return;

    this.privacy().setTrackingConsent(consent, () => this.toggleReopenLinks(true));
  }

  onClick(event) {
    const button = event.target.closest('[data-cookie-action]');
    if (!button) return;
    event.preventDefault();

    const all = (value) => ({ analytics: value, marketing: value, preferences: value });
    switch (button.dataset.cookieAction) {
      case 'accept':
        this.record(all(true));
        break;
      case 'decline':
        this.record(all(false));
        break;
      case 'save': {
        const consent = all(false);
        this.querySelectorAll('[data-cookie-purpose]').forEach((input) => {
          consent[input.dataset.cookiePurpose] = input.checked;
        });
        this.record(consent);
        break;
      }
      case 'manage':
        this.openedFrom = 'banner';
        this.open('preferences');
        break;
      case 'back':
        this.goBack();
        break;
    }
  }

  // Closing the preferences returns to the banner if it came from there, otherwise closes.
  goBack() {
    if (this.openedFrom === 'banner') {
      this.open('banner');
      const manage = this.querySelector('[data-cookie-action="manage"]');
      if (manage) manage.focus({ preventScroll: true });
    } else {
      this.close();
    }
  }

  onKeyDown(event) {
    if (event.key === 'Escape' && this.views.preferences && !this.views.preferences.hidden) {
      event.preventDefault();
      this.goBack();
    }
  }

  onReopenClick(event) {
    const link = event.target.closest('a[href$="#shopifyReshowConsentBanner"], [data-cookie-preferences]');
    if (!link) return;
    event.preventDefault();
    this.openedFrom = 'link';
    this.open('preferences');
  }

  toggleReopenLinks(show) {
    document.querySelectorAll('[data-cookie-preferences-item]').forEach((item) => {
      item.hidden = !show;
    });
  }
}
customElements.define('cookie-banner', CookieBanner);
